import React from "react";
import YouTube from "react-youtube";
import { Link, useSearchParams } from "react-router-dom";
import theme from "../theme";

const styles = {
  card: {
    padding: theme.spacing.small,
    border: `1px solid #ddd`,
    borderRadius: "3px",
    backgroundColor: theme.colors.cardBackground,
    boxShadow: `0 2px 4px ${theme.colors.cardShadow}`,
    position: "relative",
    transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
    display: "flex",
    flexDirection: "column", // Flexbox für vertikale Anordnung
    justifyContent: "space-between", // Platz zwischen Inhalt und Button
    height: "90%", // Box nimmt die volle Höhe ein
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: {
    fontSize: "18px",
    color: theme.colors.accent,
    marginBottom: theme.spacing.small,
    marginTop: "0px",
  },
  cardMain: {
    color: theme.colors.text,
  },
  detailsButton: {
    padding: "8px 12px",
    fontSize: "14px",
    color: "#fff",
    backgroundColor: theme.colors.accent,
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginTop: "auto", // Push button to the bottom
  },
  detailsText: {
    marginTop: theme.spacing.small,
    padding: theme.spacing.small,
    backgroundColor: theme.colors.background,
    color: theme.colors.textPrimary,
    borderRadius: "4px",
    fontSize: "14px",
    whiteSpace: "pre-wrap",
  },
  youtubeContainer: {
    marginTop: theme.spacing.small,
    borderRadius: "4px",
    overflow: "hidden",
  },
  difficultyLabel: {
    padding: "5px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: "bold",
    color: "#fff",
  },
  difficultyColors: {
    1: {
      backgroundColor: "rgba(144, 238, 144, 0.8)", // Leichtes Grün
    },
    2: {
      backgroundColor: "rgba(173, 216, 230, 0.8)", // Leichtes Blau
    },
    3: {
      backgroundColor: "rgba(255, 223, 186, 0.8)", // Leichtes Orange
    },
    4: {
      backgroundColor: "rgba(255, 255, 153, 0.8)", // Leichtes Gelb
    },
    5: {
      backgroundColor: "rgba(255, 182, 193, 0.8)", // Leichtes Rot
    },
  },
};

const ExerciseCard = ({ exercise, isHovered, onHover, onLeave, onToggleDetails, isSelected }) => {
  const [searchParams] = useSearchParams();

  // Aktuelle Filter-Parameter beibehalten
  const currentParams = Object.fromEntries([...searchParams]);
  const queryString = new URLSearchParams(currentParams).toString();

  // Extrahiere die YouTube-Video-ID aus der URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null; // Überprüfe, ob die URL existiert
    const match = url.match(/(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/) || 
                  url.match(/(?:https?:\/\/)?youtu\.be\/([^?]+)/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeVideoId(exercise["Short YouTube Demonstration"]);

  // Wähle die Farbe für das Difficulty-Label basierend auf dem Schwierigkeitsgrad
  const difficultyStyle = styles.difficultyColors[exercise["Difficulty Level"]] || {};

  return (
    <div
      style={{
        ...styles.card,
        ...(isHovered ? styles.cardHover : {}),
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
    >
      <div style={styles.cardHeader}>
        <h3 style={styles.cardTitle}>{exercise.Exercise}</h3>
        <div style={{ ...styles.difficultyLabel, ...difficultyStyle }}>
          {exercise["Difficulty Level"]}
        </div>
      </div>
      <div style={styles.cardMain}>
        <p><strong>Primär Muskel:</strong> {exercise["Prime Mover Muscle"]}</p>
        <p><strong>Sekundär Muskel:</strong> {exercise["Secondary Muscle"]}</p>
        <p><strong>Tertiär Muskel:</strong> {exercise["Tertiary Muscle"]}</p>
      </div>
      <Link 
        style={styles.detailsButton}
        to={`/details/${exercise.Index}?${queryString}`} 
        className="details-link"
      >
        {isSelected ? "Weniger Details" : "Mehr Details"}
      </Link>
      {isSelected && (
        <div>
          <pre style={styles.detailsText}>
            <p><strong>Bewegungsmechanik:</strong> {exercise["Mechanics"]}</p>
            <p><strong>Benötigtes Equipment:</strong><br/>{exercise["# Primary Items"]} {exercise["Primary Equipment"]}<br/>{exercise["# Secondary Items"]} {exercise["Secondary Equipment"]}</p>
                        
            <p><strong>Haltung:</strong> {exercise["Posture"]}</p>
            <p><strong>Ein oder Beidarmig:</strong> {exercise["Single or Double Arm"]}</p>
            <p><strong>abwechselnd:</strong> {exercise["Continuous or Alternating Arms"]}</p>
            <p><strong>Griff:</strong> {exercise["Grip"]}</p>
            <p><strong>Laststellung(Ende):</strong> {exercise["Load Position (Ending)"]}</p>
            <p><strong>abwechselnd Beine:</strong> {exercise["Continuous or Alternating Legs"]}</p>
            <p><strong>Fusserhöhung:</strong> {exercise["Foot Elevation"]}</p>
            <p><strong>Kombinationsübungen:</strong> {exercise["Combination Exercises"]}</p>
            <p><strong>Bewegungsmuster:</strong> {exercise["Movement Pattern #1"]}</p>
            <p><strong>Bewegungsmuster:</strong> {exercise["Movement Pattern #2"]}</p>
            <p><strong>Bewegungsmuster:</strong> {exercise["Movement Pattern #3"]}</p>
            <p><strong>Bewegungsebene:</strong> {exercise["Plane Of Motion #1"]}</p>
            <p><strong>Bewegungsebene:</strong> {exercise["Plane Of Motion #2"]}</p>
            <p><strong>Bewegungsebene:</strong> {exercise["Plane Of Motion #3"]}</p>
            <p><strong>Körperregion:</strong> {exercise["Body Region"]}</p>
            <p><strong>Kraftart:</strong> {exercise["Force Type"]}</p>
            <p><strong>Lateralität:</strong> {exercise["Laterality"]}</p>
            <p><strong>Klasse/Art:</strong> {exercise["Primary Exercise Classification"]}</p>

          </pre>
          {videoId && (
            <div style={styles.youtubeContainer}>
              <YouTube videoId={videoId} opts={{ width: "100%", height: "315" }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;